import Head from "next/head";
import { useRouter } from "next/router";
import ErrorPage from "next/error";
import {
  getAllPostsWithSlug,
  getNewsPostPaths,
  getNewsPostStaticProps,
} from "@/lib/posts";
import { Layout } from "@/components/Layout";
import PostBody from "@/components/PostBody";
import PostHeader from "@/components/PostHeader";
import PostTitle from "@/components/PostTitle";
import { SectionContainer } from "@/components/SectionContainer";
import { stripHtml } from "string-strip-html";

export const getStaticProps = async ({ params }) => {
  return getNewsPostStaticProps(params?.slug);
};

export const getStaticPaths = async () => {
  const allPosts = await getAllPostsWithSlug();

  return {
    paths: getNewsPostPaths(allPosts, "/news"),
    fallback: false,
  };
};

export default function Post({ post }) {
  const router = useRouter();

  if (router.isFallback) {
    return (
      <Layout hideCTA={true}>
        <PostTitle title="Loading...." />
      </Layout>
    );
  }

  if (!post?.slug) {
    return <ErrorPage statusCode={404} />;
  }

  const excerpt = post.excerpt ? stripHtml(post.excerpt).result : "";
  const featuredImageUrl = post.featuredImage?.node?.sourceUrl;
  const authorName = post.author?.node?.name;

  return (
    <Layout hideCTA={true}>
      <article>
        <Head>
          <title>{`${post.title} - News - Govern For California`}</title>
          <meta name="description" content={excerpt} />
          {authorName ? <meta name="author" content={authorName} /> : null}
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content="@govern4ca" />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={excerpt} />
          <meta
            property="og:image"
            content={
              featuredImageUrl ||
              "https://www.governforcalifornia.org/gfc-og.png"
            }
          />
        </Head>
        <SectionContainer bgColor="bg-white" aria-label="Post content">
          <PostHeader
            title={post.title}
            categories={post.categories}
            coverImage={post.featuredImage}
            date={post.date}
            author={post.author}
          />
          <PostBody content={post.content || ""} />
        </SectionContainer>
      </article>
    </Layout>
  );
}
